import React, { useState, useEffect, useRef } from 'react';
import { Input, Tooltip, Button, Checkbox, Dialog } from 'tdesign-react';
import { splitByCommas } from '../../../../utils/index.js';
import '../../../../style/RecognizeAgent.css';

/**
 * 创建一个空字段对象
 * @param {string} keyName 字段名
 * @param {string} [prompt=''] 字段提示
 */
function createEmptyField(keyName = '', prompt = '') {
  return {
    KeyName: keyName,
    KeyType: 0,
    KeyPrompt: prompt,
    SubItems: [],
  };
}

/**
 * 判断字段下是否存在有效子字段（KeyName 非空）
 */
function hasValidSubItems(field) {
  if (!field?.SubItems || !field.SubItems.length) return false;
  return field.SubItems.some((s) => s.KeyName && s.KeyName.trim());
}

/**
 * 根据子字段状态规范化字段（请求前调用）：
 * - 若子字段有有效 KeyName：KeyType=1 + SubItems(只保留有效项)
 * - 否则：KeyType=0 + 不带 SubItems
 */
function normalizeFieldForRequest(field) {
  if (!field || !field.KeyName || !field.KeyName.trim()) return null;
  const base = {
    KeyName: field.KeyName.trim(),
    KeyType: 0,
    KeyPrompt: field.KeyPrompt || '',
  };
  if (hasValidSubItems(field)) {
    const validSubs = field.SubItems.filter((s) => s.KeyName && s.KeyName.trim()).map(
      (sub) => {
        const subBase = {
          KeyName: sub.KeyName.trim(),
          KeyType: 0,
          KeyPrompt: sub.KeyPrompt || '',
        };
        // L3 子字段
        if (sub.SubItems && sub.SubItems.length) {
          const validL3 = sub.SubItems.filter(
            (l3) => l3.KeyName && l3.KeyName.trim(),
          );
          if (validL3.length) {
            subBase.KeyType = 1;
            subBase.SubItems = validL3.map((l3) => ({
              KeyName: l3.KeyName.trim(),
              KeyType: 0,
              KeyPrompt: l3.KeyPrompt || '',
            }));
          }
        }
        return subBase;
      },
    );
    base.KeyType = 1;
    base.SubItems = validSubs;
  }
  return base;
}

/**
 * 将 schemaItems 转换为请求参数（过滤空字段 + 规范化 KeyType）
 */
function buildSchemaItemsForRequest(schemaItems = []) {
  return schemaItems
    .map(normalizeFieldForRequest)
    .filter((f) => f !== null);
}

/* ===========================================================
 * FieldConfigDialog
 * 单个 L1 字段的编辑弹窗：L1 KeyName/KeyPrompt + L2 子字段列表 + L2 内的 L3 子字段
 * =========================================================== */
function FieldConfigDialog({ visible, field, onClose, onConfirm }) {
  // 本地编辑草稿：{ KeyName, KeyPrompt, SubItems: [{KeyName, KeyPrompt, SubItems: [...]}] }
  const buildDraft = (f) => {
    const existingSubs = (f?.SubItems || []).map((s) => ({
      KeyName: s.KeyName || '',
      KeyPrompt: s.KeyPrompt || '',
      SubItems: (s.SubItems || []).map((l3) => ({
        KeyName: l3.KeyName || '',
        KeyPrompt: l3.KeyPrompt || '',
      })),
    }));
    // 打开弹窗时若子字段为空，预置一个 L2 占位项（其内预置一个 L3 占位项）
    // 让用户直观看到一、二、三级字段结构
    let subItems = existingSubs;
    if (subItems.length === 0) {
      subItems = [createEmptyField('', '')];
      subItems[0].SubItems = [createEmptyField('', '')];
    }
    return {
      KeyName: f?.KeyName || '',
      KeyPrompt: f?.KeyPrompt || '',
      SubItems: subItems,
    };
  };

  const [draft, setDraft] = useState(() => buildDraft(field));

  useEffect(() => {
    if (visible) {
      setDraft(buildDraft(field));
    }
  }, [visible, field]);

  // 顶层 L1 字段更新
  const updateL1 = (patch) => setDraft((d) => ({ ...d, ...patch }));

  // L2 子字段更新
  const updateL2 = (l2Index, patch) =>
    setDraft((d) => {
      const next = [...d.SubItems];
      next[l2Index] = { ...next[l2Index], ...patch };
      return { ...d, SubItems: next };
    });

  // 添加 L2 子字段
  const addL2 = () =>
    setDraft((d) => ({
      ...d,
      SubItems: [
        ...d.SubItems,
        createEmptyField('', ''),
      ],
    }));

  // 删除 L2 子字段（级联删除 L3）
  const removeL2 = (l2Index) =>
    setDraft((d) => {
      const next = d.SubItems.filter((_, i) => i !== l2Index);
      return { ...d, SubItems: next };
    });

  // L3 子字段更新
  const updateL3 = (l2Index, l3Index, patch) =>
    setDraft((d) => {
      const next = d.SubItems.map((l2, i) => {
        if (i !== l2Index) return l2;
        const nextSubs = [...(l2.SubItems || [])];
        nextSubs[l3Index] = { ...nextSubs[l3Index], ...patch };
        return { ...l2, SubItems: nextSubs };
      });
      return { ...d, SubItems: next };
    });

  // 添加 L3 子字段
  const addL3 = (l2Index) =>
    setDraft((d) => {
      const next = d.SubItems.map((l2, i) => {
        if (i !== l2Index) return l2;
        return {
          ...l2,
          SubItems: [...(l2.SubItems || []), createEmptyField('', '')],
        };
      });
      return { ...d, SubItems: next };
    });

  // 删除 L3 子字段
  const removeL3 = (l2Index, l3Index) =>
    setDraft((d) => {
      const next = d.SubItems.map((l2, i) => {
        if (i !== l2Index) return l2;
        return {
          ...l2,
          SubItems: (l2.SubItems || []).filter((_, j) => j !== l3Index),
        };
      });
      return { ...d, SubItems: next };
    });

  const handleConfirm = () => {
    onConfirm({
      KeyName: draft.KeyName,
      KeyPrompt: draft.KeyPrompt,
      SubItems: draft.SubItems,
    });
  };

  return (
    <Dialog
      visible={visible}
      header="字段配置"
      onClose={onClose}
      onCancel={onClose}
      onConfirm={handleConfirm}
      width={680}
      confirmBtn="确定"
      cancelBtn="取消"
      destroyOnClose
    >
      <div className="recognize-agent-field-dialog">
        {/* L1 字段名 */}
        <div className="recognize-agent-field-dialog__row">
          <div className="recognize-agent-field-dialog__label">
            <span>字段名</span>
          </div>
          <Input
            placeholder="请输入字段名，如：教育经历"
            value={draft.KeyName}
            onChange={(v) => updateL1({ KeyName: v })}
            size="medium"
          />
        </div>

        {/* L1 KeyPrompt */}
        <div className="recognize-agent-field-dialog__row">
          <div className="recognize-agent-field-dialog__label">
            <span>字段提示</span>
            <Tooltip content="辅助模型理解如何抽取该字段" theme="dark">
              <span className="ocrDemo-icon-question"></span>
            </Tooltip>
          </div>
          <Input
            placeholder="请输入字段提示，如：按时间倒序提取"
            value={draft.KeyPrompt}
            onChange={(v) => updateL1({ KeyPrompt: v })}
            size="medium"
          />
        </div>

        {/* L2 子字段区 */}
        <div className="recognize-agent-field-dialog__subsection">
          <div className="recognize-agent-field-dialog__subsection-header">
            <span className="recognize-agent-field-dialog__subsection-title">
              二级子字段
            </span>
            <Button
              theme="default"
              variant="outline"
              size="small"
              onClick={addL2}
            >
              <span className="ocrDemo-icon_add"></span>
              <span>添加二级子字段</span>
            </Button>
          </div>

          {draft.SubItems.length === 0 && (
            <div className="recognize-agent-field-dialog__empty">
              暂无二级子字段，点击右上角「+ 添加二级子字段」开始配置
            </div>
          )}

          {draft.SubItems.map((l2, l2Index) => (
            <div
              key={l2Index}
              className="recognize-agent-field-dialog__subitem"
            >
              {/* L2 头部：序号 + 删除按钮 */}
              <div className="recognize-agent-field-dialog__subitem-header">
                <span className="recognize-agent-field-dialog__subitem-index">
                  #{l2Index + 1}
                </span>
                <span
                  className="recognize-agent-field-dialog__subitem-remove"
                  onClick={() => removeL2(l2Index)}
                  title="删除该二级字段及其全部子字段"
                >
                  ×
                </span>
              </div>

              {/* L2 KeyName + KeyPrompt 并排 */}
              <div className="recognize-agent-field-dialog__row recognize-agent-field-dialog__row--inline">
                <div className="recognize-agent-field-dialog__col">
                  <div className="recognize-agent-field-dialog__sublabel">
                    KeyName
                  </div>
                  <Input
                    placeholder="如：学期名称"
                    value={l2.KeyName}
                    onChange={(v) => updateL2(l2Index, { KeyName: v })}
                    size="medium"
                  />
                </div>
                <div className="recognize-agent-field-dialog__col">
                  <div className="recognize-agent-field-dialog__sublabel">
                    KeyPrompt
                  </div>
                  <Input
                    placeholder="子字段提示"
                    value={l2.KeyPrompt}
                    onChange={(v) => updateL2(l2Index, { KeyPrompt: v })}
                    size="medium"
                  />
                </div>
              </div>

              {/* L3 子字段区 */}
              <div className="recognize-agent-field-dialog__subsection recognize-agent-field-dialog__subsection--nested">
                <div className="recognize-agent-field-dialog__subsection-header">
                  <span className="recognize-agent-field-dialog__subsection-title">
                    三级子字段
                  </span>
                  <Button
                    theme="default"
                    variant="outline"
                    size="small"
                    onClick={() => addL3(l2Index)}
                  >
                    <span className="ocrDemo-icon_add"></span>
                    <span>添加三级子字段</span>
                  </Button>
                </div>

                {(l2.SubItems || []).length === 0 && (
                  <div className="recognize-agent-field-dialog__empty">
                    暂无三级子字段
                  </div>
                )}

                {(l2.SubItems || []).map((l3, l3Index) => (
                  <div
                    key={l3Index}
                    className="recognize-agent-field-dialog__subitem recognize-agent-field-dialog__subitem--l3"
                  >
                    <div className="recognize-agent-field-dialog__subitem-header">
                      <span className="recognize-agent-field-dialog__subitem-index">
                        #{l3Index + 1}
                      </span>
                      <span
                        className="recognize-agent-field-dialog__subitem-remove"
                        onClick={() => removeL3(l2Index, l3Index)}
                        title="删除该三级字段"
                      >
                        ×
                      </span>
                    </div>
                    <div className="recognize-agent-field-dialog__row recognize-agent-field-dialog__row--inline">
                      <div className="recognize-agent-field-dialog__col">
                        <div className="recognize-agent-field-dialog__sublabel">
                          KeyName
                        </div>
                        <Input
                          placeholder="如：学期名称"
                          value={l3.KeyName}
                          onChange={(v) =>
                            updateL3(l2Index, l3Index, { KeyName: v })
                          }
                          size="medium"
                        />
                      </div>
                      <div className="recognize-agent-field-dialog__col">
                        <div className="recognize-agent-field-dialog__sublabel">
                          KeyPrompt
                        </div>
                        <Input
                          placeholder="子字段提示"
                          value={l3.KeyPrompt}
                          onChange={(v) =>
                            updateL3(l2Index, l3Index, { KeyPrompt: v })
                          }
                          size="medium"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Dialog>
  );
}

/* ===========================================================
 * FieldSchemaEditor
 * 信息提取（QueryType=4）的字段列表编辑器
 * =========================================================== */
function FieldSchemaEditor({ value = [], onChange, enableCoord, onCoordChange }) {
  const [inputValue, setInputValue] = useState('');
  // 当前正在编辑的字段索引
  const [editingIndex, setEditingIndex] = useState(null);
  const [dialogVisible, setDialogVisible] = useState(false);
  const inputRef = useRef(null);

  // 同步外部 value 变化（用户切换 QueryType/上传图片时）
  // 这里只做受控展示，不在每次重渲染时重置 inputValue

  // 解析逗号分隔的字段名，转换为新的字段对象
  const appendFields = (rawVal) => {
    const val = typeof rawVal === 'string' ? rawVal : inputValue;
    if (!val || !val.trim()) return;
    const names = splitByCommas(val).filter((n) => n && n.trim());
    if (!names.length) return;
    const existing = new Set(value.map((f) => f.KeyName));
    const newFields = [...value];
    let added = false;
    names.forEach((name) => {
      if (!existing.has(name)) {
        newFields.push(createEmptyField(name, ''));
        added = true;
      }
    });
    if (added) {
      onChange(newFields);
      setInputValue('');
    }
  };

  const handleEnter = (val) => {
    if (val && val.trim()) appendFields(val);
  };

  const handleBlur = () => {
    if (inputValue && inputValue.trim()) appendFields(inputValue);
  };

  // 删除 L1 字段（级联删除 L2/L3）
  const removeL1 = (index) => {
    const next = value.filter((_, i) => i !== index);
    onChange(next);
  };

  // 打开齿轮弹窗
  const openConfig = (index, e) => {
    e.stopPropagation();
    setEditingIndex(index);
    setDialogVisible(true);
  };

  // 确认弹窗修改
  const handleConfigConfirm = (newField) => {
    if (editingIndex === null) return;
    const next = value.map((f, i) =>
      i === editingIndex
        ? {
            ...newField,
            KeyType: hasValidSubItems(newField) ? 1 : 0,
          }
        : f,
    );
    onChange(next);
    setDialogVisible(false);
    setEditingIndex(null);
  };

  const handleDialogClose = () => {
    setDialogVisible(false);
    setEditingIndex(null);
  };

  const editingField =
    editingIndex !== null && editingIndex < value.length
      ? value[editingIndex]
      : null;

  return (
    <div className="recognize-agent-field-editor">
      {/* 自建 tag-input 容器：标签 + 真实输入框 都在一个带边框的容器内 */}
      <div
        className="recognize-agent-tag-input"
        onClick={() => {
          // 点击容器空白处时聚焦到内部 input
          if (inputRef.current) inputRef.current.focus();
        }}
      >
        {value.map((field, index) => {
          const hasSubs = hasValidSubItems(field);
          return (
            <span
              key={index}
              className="recognize-agent-field-tag"
              onClick={(e) => e.stopPropagation()}
            >
              <span
                className="recognize-agent-field-tag__name"
                onClick={(e) => openConfig(index, e)}
                title={field.KeyName || `字段${index + 1}`}
              >
                {field.KeyName || `字段${index + 1}`}
              </span>
              <Tooltip
                content={hasSubs ? '已配置子字段' : '配置字段'}
                theme="dark"
              >
                <span
                  className="recognize-agent-field-tag__gear"
                  onClick={(e) => openConfig(index, e)}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58a.49.49 0 00.12-.61l-1.92-3.32a.488.488 0 00-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54a.484.484 0 00-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58a.49.49 0 00-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"
                      fill="currentColor"
                    />
                  </svg>
                </span>
              </Tooltip>
              <span
                className="recognize-agent-field-tag__close"
                onClick={(e) => {
                  e.stopPropagation();
                  removeL1(index);
                }}
                title="删除该字段"
              >
                ×
              </span>
            </span>
          );
        })}
        <input
          ref={inputRef}
          className="recognize-agent-tag-input__input"
          value={inputValue}
          placeholder={value.length ? '' : '输入字段名，多个字段用逗号分隔'}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && inputValue && inputValue.trim()) {
              e.preventDefault();
              handleEnter(inputValue);
            }
          }}
          onBlur={handleBlur}
        />
      </div>

      {/* 字段配置弹窗 */}
      <FieldConfigDialog
        visible={dialogVisible}
        field={editingField}
        onClose={handleDialogClose}
        onConfirm={handleConfigConfirm}
      />

      {/* 返回坐标信息 */}
      <div className="recognize-agent-field-editor__coord">
        <Checkbox checked={!!enableCoord} onChange={onCoordChange}>
          返回坐标信息
        </Checkbox>
      </div>
    </div>
  );
}

export { FieldSchemaEditor, buildSchemaItemsForRequest, createEmptyField };
export default FieldSchemaEditor;
